import * as React from "react";
import {useCallback} from "react";
import Icon from "../ui/Icon";
import CustomButton from "../ui/CustomButton";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Checkbox from "@mui/material/Checkbox";
import TableCell from "@mui/material/TableCell";
import Box from "@mui/material/Box";
import TableSortLabel from "@mui/material/TableSortLabel";
import {visuallyHidden} from "@mui/utils";
import useMediaQuery from "@mui/material/useMediaQuery";
import {Theme, useTheme} from "@mui/material/styles";
import {Order, TableColumnsWithFilter} from "./_interfaces";
import {SxProps} from "@mui/material";

interface CellFilterButtonProps {
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
}

const CellFilterButton = ({onClick} : CellFilterButtonProps) => {
    return (
        <CustomButton
            className="filterIcon"
            buttonType="icon"
            appearance="circle"
            size="small"
            width={20}
            height={20}
            sxExtra={{
                cursor: "pointer",
                opacity: 0,
                visibility: "hidden",
                transition: "opacity 0.2s ease",
                color: "secondary.main",
                backgroundColor: "transparent",
                "&:hover": {
                    color: "secondary.light",
                    backgroundColor: "transparent",
                },
                "&[aria-expanded='true']": {
                    backgroundColor: "white",
                },
            }}
            onClick={onClick}
            icon={<Icon icon="fas bars-filter" fixedWidth={true} sx={{fontSize: "1rem"}}/>}
        />
    )
}

const getCellSx = (
    column: TableColumnsWithFilter,
    dense: boolean,
    isMdUp: boolean,
) : SxProps<Theme> => ({
    lineHeight: 1.1,
    whiteSpace: column.noWrap ? "nowrap" : "normal",
    padding: dense ? "4px" : column.disablePadding ? 0 : column.sticky ? "0 0 0 12px" : "8px 12px",
    display: column.hidden ? "none" : "table-cell",
    position: column.sticky ? "sticky" : "relative",
    right: column.sticky && isMdUp ? 0 : "auto",
    zIndex: column.sticky && isMdUp ? 2 : "auto",
    backgroundColor: column.sticky ? "background.paper" : "inherit",
    ...(column.sticky && isMdUp && {
        "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            height: "100%",
            width: "1px",
            backgroundColor: "rgba(253, 253, 253, 1)",
            boxShadow: "-2px 0px 10px 0px #98C0FF",
        },
    })
});

const getHeaderContentSx = (column: TableColumnsWithFilter): SxProps<Theme> => ({
    width: column.width ?? "auto",
    maxWidth: column.width ?? "auto",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: column.numeric ? "row-reverse" : "row",
    cursor: column.sortable ? "pointer" : "default",
    "&:hover .filterIcon": {
        opacity: 1,
        visibility: "visible",
    },
});

const getSortLabelSx = (column: TableColumnsWithFilter): SxProps<Theme> => ({
    lineHeight: 1.1,
    color: "secondary.light",
    display: "inline-flex",
    flexDirection: column.numeric ? "row-reverse" : "row",
    "& .MuiTableSortLabel-icon": {
        marginRight: column.numeric ? "4px" : 0,
        marginLeft: column.numeric ? 0 : "4px",
    },
    "&:hover": {
        color: "primary.main",
    },
});

interface EnhancedTableProps {
    onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
    order: Order;
    orderBy: string;
    rowCount: number;
    columns: TableColumnsWithFilter[];
    numSelected: number;
    onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
    hasSelect: boolean;
    dense: boolean;
    onOpenFilter?: (event: React.MouseEvent<HTMLElement>, column: TableColumnsWithFilter) => void;
    hasFilters?: boolean;
}

const EnhancedTableHead = ({
                               order,
                               orderBy,
                               onRequestSort,
                               columns,
                               onSelectAllClick,
                               numSelected,
                               rowCount,
                               hasSelect = false,
                               dense = false,
                               onOpenFilter,
                               hasFilters = false,
                           }: EnhancedTableProps) => {
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

    const createSortHandler = useCallback(
        (property: string) => (event: React.MouseEvent<unknown>) => {
            onRequestSort(event, property);
        }, [onRequestSort]);

    const handleFilterOpen = useCallback(
        (event: React.MouseEvent<HTMLElement>, column: TableColumnsWithFilter) => {
            event.stopPropagation();
            onOpenFilter?.(event, column);
        }, [onOpenFilter]);

    return (
        <TableHead>
            <TableRow sx={{ borderBottom: "1px solid rgba(253, 253, 253, 1)",}}>
                {hasSelect && (
                    <TableCell padding="checkbox">
                        <Checkbox
                            color="primary"
                            indeterminate={numSelected > 0 && numSelected < rowCount}
                            checked={rowCount > 0 && numSelected === rowCount}
                            onChange={onSelectAllClick}
                            inputProps={{'aria-label': 'select all desserts',}}
                        />
                    </TableCell>
                )}

                {columns.map((column) => (
                    <TableCell
                        key={column.id}
                        align={column.numeric ? "right" : "left"}
                        sortDirection={orderBy === column.id ? order : false}
                        sx={getCellSx(column, dense, isMdUp)}
                    >
                        <Box width={"100%"} display={"flex"} justifyContent={column.numeric ? "right" : "left"}>
                            <Box
                                gap={0}
                                sx={getHeaderContentSx(column)}
                            >
                                {column.sortable && (
                                    <TableSortLabel
                                        active={orderBy === column.id}
                                        direction={orderBy === column.id ? order : "asc"}
                                        onClick={createSortHandler(column.id)}
                                        sx={getSortLabelSx(column)}
                                    >
                                        {column.label}
                                        {orderBy === column.id ? (
                                            <Box component="span" sx={visuallyHidden}>
                                                {order === "desc" ? "sorted descending" : "sorted ascending"}
                                            </Box>
                                        ) : null}
                                    </TableSortLabel>
                                )}
                                {hasFilters && column.filter && (
                                    <CellFilterButton
                                        onClick={(event) => handleFilterOpen(event, column)}
                                    />
                                )}
                                {!column.filter && !column.sortable && (
                                    column.label
                                )}
                            </Box>
                        </Box>

                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

export default EnhancedTableHead;
